import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';

const s = {
  wrap: { minHeight:'100vh', background:'#f4ede0', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px', fontFamily:"'DM Sans',sans-serif" },
  card: { background:'#fff', borderRadius:'20px', padding:'32px', boxShadow:'0 8px 32px rgba(15,64,35,.1)' },
  input: { padding:'12px 16px', border:'1.5px solid #e5ddc9', borderRadius:'10px', fontSize:'14px', outline:'none', width:'100%', fontFamily:"'DM Sans',sans-serif", background:'#fafaf8', boxSizing:'border-box' },
  btn: { padding:'14px', borderRadius:'12px', border:'none', background:'#14562e', color:'#fff', fontSize:'15px', fontWeight:700, cursor:'pointer', width:'100%', fontFamily:"'DM Sans',sans-serif" },
  link: { background:'none', border:'none', color:'#14562e', fontSize:'13px', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", padding:0 },
  err: { background:'#fef2f2', color:'#c94a2f', padding:'12px', borderRadius:'10px', fontSize:'14px' },
  toggle: {
    display:'flex', alignItems:'center', gap:'12px', padding:'14px 16px',
    background:'#f4ede0', borderRadius:'12px', cursor:'pointer',
    border:'1.5px solid transparent', transition:'all 0.15s',
  },
  toggleActive: {
    background:'#e6f0e9', borderColor:'#14562e',
  },
  toggleSwitch: {
    width:'40px', height:'22px', borderRadius:'999px', background:'#ccc',
    position:'relative', transition:'background 0.2s', flexShrink:0,
  },
  toggleSwitchActive: {
    background:'#14562e',
  },
  toggleKnob: {
    width:'18px', height:'18px', borderRadius:'50%', background:'#fff',
    position:'absolute', top:'2px', left:'2px', transition:'transform 0.2s',
    boxShadow:'0 1px 3px rgba(0,0,0,.2)',
  },
  toggleKnobActive: {
    transform:'translateX(18px)',
  },
  proFields: {
    display:'flex', flexDirection:'column', gap:'12px',
    padding:'14px 0 0', marginTop:'4px',
    animation:'slideDown .2s ease',
  },
  proLabel: {
    fontSize:'12px', fontWeight:600, color:'#6a7268', textTransform:'uppercase',
    letterSpacing:'0.8px', marginBottom:'4px',
  },
};

// [NEW] Mesma validação do backend (server.js, isSafeRedirectPath) — garante que só
// navegamos de volta pra um path relativo do próprio app depois do login (ex:
// /repasse/:token), nunca pra uma URL externa que alguém tenha colado num link
// tipo /login?redirect=https://evil.com.
function isSafeRedirectPath(p) {
  return typeof p === 'string' && p.length > 1 && p.startsWith('/') && !/^\/[\\/]/.test(p);
}

/* ── Eye icons ─────────────────────────────────── */
function EyeOpen() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6a7268" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function EyeClosed() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6a7268" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
    </svg>
  );
}

/* ── Password input with toggle ────────────────── */
function PasswordInput({ value, onChange, placeholder = '••••••••', required = true }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        style={{ ...s.input, paddingRight: '44px' }}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        tabIndex={-1}
        style={{
          position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: 0.7, transition: 'opacity 0.15s',
        }}
        onMouseOver={e => { e.currentTarget.style.opacity = '1'; }}
        onMouseOut={e => { e.currentTarget.style.opacity = '0.7'; }}
        aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
      >
        {show ? <EyeClosed /> : <EyeOpen />}
      </button>
    </div>
  );
}

function ResetPassword({ token }) {
  const [pw, setPw] = useState('');
  const [cf, setCf] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== cf) { setErr('Senhas não coincidem'); return; }
    setLoading(true);
    try { await authAPI.resetPassword(token, pw); setDone(true); setTimeout(() => navigate('/login'), 2000); }
    catch { setErr('Link inválido ou expirado'); }
    finally { setLoading(false); }
  };

  return (
    <div style={s.wrap}>
      <div style={{ width:'100%', maxWidth:'420px' }}>
        <div style={{ textAlign:'center', marginBottom:'32px' }}>
          <img src="/nico-1.png" alt="NICO" style={{ height:'64px' }} />
        </div>
        <div style={s.card}>
          {done ? (
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:'40px' }}>✅</div>
              <p style={{ color:'#6a7268' }}>Senha redefinida! Redirecionando...</p>
            </div>
          ) : (
            <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
              <h3 style={{ color:'#183d24', margin:0 }}>Nova senha</h3>
              {err && <div style={s.err}>{err}</div>}
              <PasswordInput value={pw} onChange={e => setPw(e.target.value)} placeholder="Nova senha" />
              <PasswordInput value={cf} onChange={e => setCf(e.target.value)} placeholder="Confirmar senha" />
              <button type="submit" disabled={loading} style={{ ...s.btn, opacity: loading ? .7 : 1 }}>
                {loading ? 'Salvando...' : 'Salvar senha'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isPro, setIsPro] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const initialTab = params.get('tab');
  const initialPro = params.get('pro') === 'true';
  const [mode, setMode] = useState(initialTab === 'register' ? 'register' : 'login');

  // [NEW] Pra onde voltar depois de logar (ex: /repasse/:token, quando o usuário veio de
  // um link de repasse de veículo sem estar autenticado ainda). Ausente/segue pra
  // /garagem como sempre fez, se não vier nada ou vier algo que não pareça um path
  // relativo do próprio app.
  const redirectParam = params.get('redirect');
  const safeRedirectTarget = isSafeRedirectPath(redirectParam) ? redirectParam : null;

  // Ativa toggle pro se veio da LP de oficinas
  React.useEffect(() => { if (initialPro) setIsPro(true); }, [initialPro]);

  // Google OAuth callback
  React.useEffect(() => {
    if (params.get('google') === 'success') {
      const t = params.get('token');
      const rt = params.get('refreshToken');
      // [NEW] Lido ANTES do setParams({}) abaixo, que limpa a query string (e junto ela
      // some do resultado de params.get em qualquer chamada futura nesse render).
      const postGoogleRedirect = params.get('redirect');
      if (t && rt) {
        localStorage.setItem('nico_token', t);
        localStorage.setItem('nico_refresh', rt);
        setParams({});
        navigate(isSafeRedirectPath(postGoogleRedirect) ? postGoogleRedirect : '/garagem', { replace: true });
        window.location.reload();
      }
    }
    if (params.get('error')) {
      const errorMap = {
        google_no_code: 'Erro na autenticação com Google. Tente novamente.',
        google_failed: 'Falha ao conectar com Google. Tente novamente.',
        account_inactive: 'Sua conta está inativa ou suspensa.',
      };
      setErr(errorMap[params.get('error')] || 'Erro ao fazer login com Google.');
      setParams({});
    }
  }, []);

  const API_URL = process.env.REACT_APP_API_URL || 'https://nico-548x.onrender.com/api';
  // [NEW] Encaminha o destino de pós-login pro backend, que o embute no `state` do Google
  // e devolve no callback — sem isso, `?redirect=` só sobrevivia até o clique em "Entrar
  // com Google", porque a ida e volta pelo Google não carregava esse dado junto.
  const googleLoginUrl = API_URL + '/auth/google' + (safeRedirectTarget ? `?redirect=${encodeURIComponent(safeRedirectTarget)}` : '');

  // Reset password — render AFTER all hooks
  const token = params.get('token');
  if (token && !params.get('google')) return <ResetPassword token={token} />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        if (!acceptedTerms) { setErr('Você precisa aceitar os termos para continuar'); setLoading(false); return; }
        const role = isPro ? 'professional' : 'owner';
        await register(name, email, password, role, phone || undefined, isPro ? businessName : undefined);
      }
      // [NEW] Login/cadastro por email+senha também respeita ?redirect= agora — antes
      // sempre mandava pra /garagem, mesmo vindo de um link como o de repasse de veículo.
      navigate(safeRedirectTarget || '/garagem');
    } catch(e) { setErr(e.message || 'Erro ao entrar'); }
    finally { setLoading(false); }
  };

  const forgot = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try { await authAPI.forgotPassword(email); setForgotSent(true); }
    catch { setErr('Erro ao enviar email'); }
    finally { setLoading(false); }
  };

  return (
    <div style={s.wrap}>
      <div style={{ width:'100%', maxWidth:'420px' }}>
        <div style={{ textAlign:'center', marginBottom:'32px' }}>
          <img src="/nico-1.png" alt="NICO" style={{ height:'64px', width:'auto', objectFit:'contain' }} />
        </div>
        <div style={s.card}>
          {mode !== 'forgot' ? (
            <>
              <div style={{ display:'flex', background:'#f4ede0', borderRadius:'12px', padding:'4px', marginBottom:'24px' }}>
                {['login','register'].map(m => (
                  <button key={m} type="button" onClick={() => { setMode(m); setErr(''); setIsPro(false); setAcceptedTerms(false); }}
                    style={{ flex:1, padding:'10px', borderRadius:'10px', border:'none', cursor:'pointer',
                      fontFamily:"'DM Sans',sans-serif", fontWeight:600, fontSize:'14px',
                      background: mode===m ? '#fff' : 'transparent',
                      color: mode===m ? '#14562e' : '#6a7268',
                      boxShadow: mode===m ? '0 1px 4px rgba(0,0,0,.1)' : 'none' }}>
                    {m === 'login' ? 'Entrar' : 'Criar conta'}
                  </button>
                ))}
              </div>
              <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
                {err && <div style={s.err}>{err}</div>}
                {mode === 'register' && (
                  <input placeholder="Nome completo" value={name} onChange={e => setName(e.target.value)} required style={s.input} />
                )}
                <input type="email" placeholder="seu@email.com" value={email} onChange={e => setEmail(e.target.value)} required style={s.input} />
                <PasswordInput value={password} onChange={e => setPassword(e.target.value)} />

                {/* Professional toggle */}
                {mode === 'register' && (
                  <>
                    <div
                      style={{ ...s.toggle, ...(isPro ? s.toggleActive : {}) }}
                      onClick={() => setIsPro(!isPro)}
                    >
                      <div style={{ ...s.toggleSwitch, ...(isPro ? s.toggleSwitchActive : {}) }}>
                        <div style={{ ...s.toggleKnob, ...(isPro ? s.toggleKnobActive : {}) }} />
                      </div>
                      <div>
                        <div style={{ fontSize:'14px', fontWeight:600, color:'#14562e' }}>
                          🔧 Sou mecânico ou tenho uma oficina
                        </div>
                        <div style={{ fontSize:'12px', color:'#6a7268', marginTop:'2px' }}>
                          Crie uma conta profissional para validar serviços
                        </div>
                      </div>
                    </div>

                    {isPro && (
                      <div style={s.proFields}>
                        <div>
                          <label style={s.proLabel}>Nome da oficina / razão social</label>
                          <input
                            placeholder="Ex: Auto Center Silva"
                            value={businessName}
                            onChange={e => setBusinessName(e.target.value)}
                            style={s.input}
                          />
                        </div>
                        <div>
                          <label style={s.proLabel}>Telefone / WhatsApp</label>
                          <input
                            placeholder="(11) 99999-9999"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            style={s.input}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}

                {mode === 'login' && (
                  <button type="button" onClick={() => { setMode('forgot'); setErr(''); }} style={{ ...s.link, textAlign:'right' }}>
                    Esqueci minha senha
                  </button>
                )}

                {mode === 'register' && (
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', padding: '8px 0' }}>
                    <div
                      onClick={() => setAcceptedTerms(!acceptedTerms)}
                      style={{
                        width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0, marginTop: '1px',
                        border: acceptedTerms ? 'none' : '2px solid #e5ddc9',
                        background: acceptedTerms ? '#14562e' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.15s', cursor: 'pointer',
                      }}
                    >
                      {acceptedTerms && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    </div>
                    <span style={{ fontSize: '13px', color: '#6a7268', lineHeight: 1.5 }}>
                      Li e concordo com os{' '}
                      <a href="/termos" target="_blank" rel="noreferrer" style={{ color: '#14562e', fontWeight: 600, textDecoration: 'underline' }}>
                        Termos de Uso
                      </a>{' '}e a{' '}
                      <a href="/privacidade" target="_blank" rel="noreferrer" style={{ color: '#14562e', fontWeight: 600, textDecoration: 'underline' }}>
                        Política de Privacidade
                      </a>{' '}do NICO. Seus dados estão protegidos conforme a LGPD.
                    </span>
                  </label>
                )}

                <button type="submit" disabled={loading || (mode === 'register' && !acceptedTerms)} style={{ ...s.btn, opacity: (loading || (mode === 'register' && !acceptedTerms)) ? .5 : 1 }}>
                  {loading ? 'Aguarde...' : mode === 'login' ? 'Entrar' : isPro ? 'Criar conta profissional' : 'Criar conta'}
                </button>

                {/* Separador */}
                <div style={{ display:'flex', alignItems:'center', gap:'12px', margin:'4px 0' }}>
                  <div style={{ flex:1, height:'1px', background:'#e5ddc9' }} />
                  <span style={{ fontSize:'12px', color:'#6a7268', fontWeight:500 }}>ou</span>
                  <div style={{ flex:1, height:'1px', background:'#e5ddc9' }} />
                </div>

                {/* Google Login */}
                <a href={(mode === 'register' && !acceptedTerms) ? undefined : googleLoginUrl}
                  onClick={e => { if (mode === 'register' && !acceptedTerms) { e.preventDefault(); setErr('Você precisa aceitar os termos para continuar'); } }}
                  style={{
                  display:'flex', alignItems:'center', justifyContent:'center', gap:'10px',
                  padding:'12px', borderRadius:'12px', border:'1.5px solid #e5ddc9',
                  background:'#fff', textDecoration:'none', cursor: (mode === 'register' && !acceptedTerms) ? 'not-allowed' : 'pointer',
                  fontFamily:"'DM Sans',sans-serif", fontSize:'14px', fontWeight:600,
                  color:'#183d24', transition:'background 0.15s, border-color 0.15s',
                  opacity: (mode === 'register' && !acceptedTerms) ? 0.5 : 1,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  {mode === 'login' ? 'Entrar com Google' : 'Criar conta com Google'}
                </a>
              </form>
            </>
          ) : forgotSent ? (
            <div style={{ textAlign:'center', padding:'20px 0' }}>
              <div style={{ fontSize:'40px', marginBottom:'16px' }}>📧</div>
              <p style={{ color:'#6a7268', marginBottom:'20px' }}>Verifique seu email!</p>
              <button onClick={() => { setMode('login'); setForgotSent(false); }} style={s.btn}>Voltar ao login</button>
            </div>
          ) : (
            <form onSubmit={forgot} style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
              <h3 style={{ color:'#183d24', margin:0 }}>Redefinir senha</h3>
              {err && <div style={s.err}>{err}</div>}
              <input type="email" placeholder="seu@email.com" value={email} onChange={e => setEmail(e.target.value)} required style={s.input} />
              <button type="submit" disabled={loading} style={{ ...s.btn, opacity: loading ? .7 : 1 }}>
                {loading ? 'Enviando...' : 'Enviar link'}
              </button>
              <button type="button" onClick={() => setMode('login')} style={{ ...s.link, textAlign:'center' }}>
                ← Voltar
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
